export const CHALLENGE_BUILDER_STEPS = [
  'goal',
  'objectives',
  'tasks',
  'metrics',
  'targets',
  'schedule',
  'finish',
] as const;

export const CHALLENGE_BUILDER_EXP_STEPS = ['expGoal'] as const;

export type ChallengeStep = (typeof CHALLENGE_BUILDER_STEPS)[number];

export enum ChallengeBuilderSteps {
  REFINE_GOAL = 'refineGoal',
  GOAL = 'goal',
  OBJECTIVES = 'objectives',
  TASKS = 'tasks',
  METRICS = 'metrics',
  TARGETS = 'targets',
  SCHEDULE = 'schedule',
  OVERVIEW = 'overview',
}

interface CheckTip {
  type: 'check';
  title: string;
  description: string;
}

interface BulletTip {
  type: 'bullet';
  title: string;
  bulletPoints: string[];
}

interface BooleanTip {
  type: 'boolean';
  title: string;
  description: string;
  examples: {
    label: string;
    booleanType: 'yes-active' | 'no-active'; // yes-active = blue on "no", no-active = blue on "yes"
  }[];
}

interface NumericTip {
  type: 'numeric';
  title: string;
  description: string;
  examples: {
    label: string;
    value: string;
    unit: string;
  }[];
}

interface PercentageTip {
  type: 'percentage';
  title: string;
  description: string;
  examples: {
    label: string;
    value: number; // 0-100
  }[];
}

interface DurationTip {
  type: 'duration';
  title: string;
  description: string;
  examples: {
    label: string;
    hours?: string;
    minutes?: string;
    seconds?: string;
  }[];
}

interface TextTip {
  type: 'text';
  description: string;
}

export type Tip = CheckTip | BulletTip | BooleanTip | NumericTip | PercentageTip | DurationTip | TextTip;

export interface ChallengeBuilderStepMapItem {
  [key: string]: {
    label: string;
    color: string;
    icon: string;
    titleText: string;
    subtitleText: string;
    nextButtonText?: string;
    tipHeaderText: string;
    tips: Tip[];
  };
}

const TASKS_TIPS: Tip[] = [
  {
    type: 'bullet',
    title: 'Health & Fitness',
    bulletPoints: [
      '"Improve my physical fitness"',
      '"Develop sustainable eating habits"',
      '"Build consistent energy levels"',
    ],
  },
  {
    type: 'bullet',
    title: 'Work & Productivity',
    bulletPoints: ['"Improve my focus at work"', '"Develop better time management"', '"Build deep work capabilities"'],
  },
  {
    type: 'bullet',
    title: 'Family & Relationships',
    bulletPoints: ['"Be a more present parent"', '"Strengthen my relationships"', '"Create quality family time"'],
  },
  {
    type: 'bullet',
    title: 'Personal Growth',
    bulletPoints: ['"Become more mindful"', '"Develop a learning habit"', '"Build creative consistency"'],
  },
];

export const CHALLENGE_BUILDER_STEP_MAP: ChallengeBuilderStepMapItem = {
  //-------------------------- Goal --------------------------
  goal: {
    label: ChallengeBuilderSteps.GOAL,
    color: '#ffd3fa',
    icon: 'goal.png',
    titleText: 'Your infinite goal',
    subtitleText:
      "This is your aspirational direction. It's not a finish line - it's the \"infinite game\" you're playing for the joy of playing.",
    nextButtonText: 'Add Objectives',
    tipHeaderText: 'What makes a good goal?',
    tips: [
      {
        type: 'check',
        title: 'Aspirational but Relatable',
        description: '"Be healthier" is good. "Look like a supermodel" might not be.',
      },
      {
        type: 'check',
        title: 'No Finish Line',
        description: 'You can always be more present, healthier, or more productive.',
      },
      {
        type: 'check',
        title: 'Personally Meaningful',
        description: 'Choose something you care about, not what others expect.',
      },
      ...TASKS_TIPS,
    ],
  },
  //-------------------------- Objectives --------------------------
  objectives: {
    label: ChallengeBuilderSteps.OBJECTIVES,
    color: '#edd3ff',
    icon: 'objectives.png',
    titleText: 'Define your objectives',
    subtitleText: 'Your objectives are broad, directional goals that move you toward your infinite goal..',
    nextButtonText: 'Add tasks',
    tipHeaderText: 'Creating objectives.',
    tips: [
      {
        type: 'check',
        title: 'Broad and Directional',
        description: "Point toward what you want, not how you'll get there.",
      },
      {
        type: 'check',
        title: 'Not Too Specific',
        description: 'Leave room for defining the specific behavior changes later.',
      },
      {
        type: 'check',
        title: 'Clear Purpose',
        description: 'Each objective should clearly support your infinite goal',
      },
      ...TASKS_TIPS,
    ],
  },
  //-------------------------- Tasks --------------------------
  tasks: {
    label: ChallengeBuilderSteps.TASKS,
    color: '#d9d3ff',
    icon: 'tasks.png',
    titleText: 'Create the tasks',
    subtitleText:
      'For each objective, identify what needs to change. What current behavior needs to stop or reduce? What new behavior needs to start?',
    nextButtonText: 'Add metrics',
    tipHeaderText: 'What makes a good task?',
    tips: [
      {
        type: 'check',
        title: 'State What Changes',
        description: "Focus on what you'll stop/reduce and what you'll start/increase.",
      },
      {
        type: 'check',
        title: 'Be Concrete & Measurable',
        description: '"3 strength sessions per week" not "exercise more."',
      },
      {
        type: 'check',
        title: 'Link to Your Objectives',
        description: 'Each task should directly support one of your objectives.',
      },
      {
        type: 'check',
        title: 'Make It Trackable',
        description: 'Can you count it? Can you observe it? Good!',
      },
      ...TASKS_TIPS,
    ],
  },
  //-------------------------- Metrics --------------------------
  metrics: {
    label: ChallengeBuilderSteps.METRICS,
    color: '#d3dfff',
    icon: 'metrics.png',
    titleText: 'Add tracking metrics',
    subtitleText: "What will you measure for each task? These are the specific things you'll track daily.",
    nextButtonText: 'Add targets',
    tipHeaderText: 'About metrics.',
    tips: [
      {
        type: 'boolean',
        title: 'Yes/No',
        description: "The simplest metric, you'll simply enter a yes or a no to the condition.",
        examples: [
          {
            label: 'Workout',
            booleanType: 'yes-active',
          },
          {
            label: 'Feeling good?',
            booleanType: 'no-active',
          },
        ],
      },
      {
        type: 'numeric',
        title: 'Amount',
        description: 'This will be a number, like 100 grams, 10 lbs, or 30 reps.',
        examples: [
          {
            label: 'Protein',
            value: '140',
            unit: 'grams',
          },
          {
            label: 'Bicep',
            value: '30',
            unit: 'reps',
          },
        ],
      },
      {
        type: 'percentage',
        title: 'Percentage',
        description: 'Allows you to track progress toward 100%.',
        examples: [
          {
            label: 'Book',
            value: 33,
          },
        ],
      },
      {
        type: 'duration',
        title: 'Time/Duration',
        description: 'The value will be expressed in hours, minutes, and seconds.',
        examples: [
          {
            label: 'Lap Time',
            hours: 'hr',
            minutes: 'min',
            seconds: 'sec',
          },
          {
            label: 'Cardio',
            hours: 'hr',
            minutes: 'min',
          },
        ],
      },
    ],
  },
  //-------------------------- Targets --------------------------
  targets: {
    label: ChallengeBuilderSteps.TARGETS,
    color: '#d3ebff',
    icon: 'target.png',
    titleText: 'Set your targets.',
    subtitleText: 'Set cycle or daily goals for you tracked metrics.',
    nextButtonText: 'Schedule tasks',
    tipHeaderText: 'What makes a good target?',
    tips: [
      {
        type: 'check',
        title: 'State What Changes',
        description: "Focus on what you'll stop/reduce and what you'll start/increase.",
      },
      {
        type: 'check',
        title: 'Be Concrete & Measurable',
        description: '"3 strength sessions per week" not "exercise more."',
      },
      {
        type: 'check',
        title: 'Link to Your Objectives',
        description: 'Each behavior change should directly support one of your objectives.',
      },
      {
        type: 'check',
        title: 'Make It Trackable',
        description: 'Can you count it? Can you observe it? Good!',
      },
    ],
  },
  //-------------------------- Schedule --------------------------
  schedule: {
    label: ChallengeBuilderSteps.SCHEDULE,
    color: '#d3f3ff',
    icon: 'schedule.png',
    titleText: 'Schedule your tasks.',
    subtitleText: 'Create opportunities to record your activity throughout the cycle.',
    tipHeaderText: 'What makes a good task schedule?',
    tips: [
      {
        type: 'text',
        description: 'Tasks are your opportunity to record your activities.',
      },
      {
        type: 'check',
        title: 'Mark a task as complete',
        description: "Focus on what you'll stop/reduce and what you'll start/increase.",
      },
      {
        type: 'check',
        title: 'Record trackable values',
        description: '"3 strength sessions per week" not "exercise more."',
      },
    ],
  },
  //-------------------------- Finish --------------------------
  finish: {
    label: ChallengeBuilderSteps.OVERVIEW,
    color: '#d3f3ff',
    icon: 'overview.png',
    titleText: 'Your overview',
    subtitleText: 'Your overview',
    tipHeaderText: 'Your overview',
    tips: [],
  },
};
